import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { trackWhatsAppClick } from '../utils/analytics';

/**
 * WhatsAppFloat
 * Floating WhatsApp CTA button — high-conversion + SEO friendly contact point.
 * Appears with a delay so it doesn't compete with the hero on first view.
 */
const WhatsAppFloat = () => {
  const [visible, setVisible] = useState(false);
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 2500);
    const t2 = setTimeout(() => setShowTip(true), 5000);
    const t3 = setTimeout(() => setShowTip(false), 12000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  const phone = '5545988068508';
  const message = encodeURIComponent(
    'Olá! Vim pelo site da Coutin Agência e gostaria de saber mais sobre os serviços.'
  );
  const href = `https://wa.me/${phone}?text=${message}`;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.6, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.6, y: 40 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          className="fixed bottom-5 left-5 z-[60] flex items-end gap-3"
          data-testid="whatsapp-float"
        >
          {/* Tooltip */}
          <AnimatePresence>
            {showTip && (
              <motion.div
                initial={{ opacity: 0, x: -10, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -10, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="hidden md:flex items-center mb-3 px-4 py-2.5 rounded-2xl rounded-bl-sm bg-white text-[#0A0A0A] text-sm font-medium shadow-2xl whitespace-nowrap relative"
              >
                Fale com a Coutin agora
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowTip(false);
                  }}
                  className="ml-2 text-[#0A0A0A]/40 hover:text-[#0A0A0A] text-xs"
                  data-testid="whatsapp-tip-close"
                  aria-label="Fechar"
                >
                  ✕
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Button */}
          <motion.a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick('whatsapp-float')}
            className="relative w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center shadow-[0_8px_32px_rgba(37,211,102,0.55)] focus:outline-none focus:ring-4 focus:ring-[#25D366]/40"
            style={{
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
            }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.94 }}
            aria-label="Conversar no WhatsApp"
            data-testid="whatsapp-float-button"
          >
            {/* Pulsing ring */}
            <motion.span
              className="absolute inset-0 rounded-full border-2 border-[#25D366]"
              animate={{ scale: [1, 1.6, 1.6], opacity: [0.7, 0, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
            />
            {/* WhatsApp icon */}
            <svg
              viewBox="0 0 24 24"
              fill="white"
              className="w-7 h-7 md:w-8 md:h-8 relative z-10"
              aria-hidden="true"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.057 0H12c-6.627 0-12 5.373-12 12 0 2.129.546 4.13 1.506 5.876L.057 24l6.305-1.654C8.075 23.439 10.003 24 12 24h.057c6.627 0 12-5.373 12-12 0-6.627-5.373-12-11.943-12zM12 22.067h-.045c-1.78 0-3.515-.477-5.024-1.379l-.36-.213-3.737.98 1-3.644-.235-.374A9.913 9.913 0 012.067 12C2.067 6.527 6.527 2.067 12 2.067c2.654 0 5.143 1.034 7.022 2.911A9.864 9.864 0 0121.933 12c0 5.473-4.46 9.933-9.933 9.933z" />
            </svg>
          </motion.a>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WhatsAppFloat;
